/**
 * Smart dedup for multi-select delete operations.
 *
 * When deleting nodes and links simultaneously, some link removals are redundant:
 * - If a link's TARGET is being deleted → node deletion already removes all parent references
 * - If a link's SOURCE is being deleted → the source node (and all its children refs) is gone
 *
 * This function filters out those redundant link removals, reducing unnecessary
 * server-side processing.
 *
 * @param nodeIds - Node IDs to delete (root should already be filtered out)
 * @param edges - Edges (links) to remove
 * @returns Deduped result: { nodeIds, edges, removedCount }
 */
export function deduplicateDeleteSelection(
    nodeIds: string[],
    edges: Array<{ sourceNodeId: string; targetNodeId: string; answerText: string }>
): {
    nodeIds: string[];
    edges: Array<{ sourceNodeId: string; targetNodeId: string; answerText: string }>;
    removedCount: number;
} {
    if (nodeIds.length === 0) {
        // No nodes being deleted → no dedup possible, all edges are meaningful
        return { nodeIds, edges, removedCount: 0 };
    }

    const nodeIdSet = new Set(nodeIds);
    const dedupedEdges = edges.filter(e =>
        !nodeIdSet.has(e.targetNodeId) && !nodeIdSet.has(e.sourceNodeId)
    );

    return {
        nodeIds,
        edges: dedupedEdges,
        removedCount: edges.length - dedupedEdges.length
    };
}
